import { useCallback } from "react";

import type { BaseQueryState } from "../utils/queryStateUtils";

type UseTableControlsOptions<T extends object> = {
  queryState: BaseQueryState<T>;
  updateQueryState: (newState: BaseQueryState<T>) => void;
};

/**
 * Shared hook for table sorting and pagination handling.
 * Provides handlers for sort changes and page navigation.
 */
export function useTableControls<T extends object>({
  queryState,
  updateQueryState,
}: UseTableControlsOptions<T>) {
  const handleSortChange = useCallback(
    (opts: { fieldName: string & keyof T; direction: "asc" | "desc" }) => {
      updateQueryState({
        ...queryState,
        query: {
          ...queryState.query,
          sort: { fieldName: opts.fieldName, direction: opts.direction },
        },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  const handlePrevPage = useCallback(() => {
    const newOffset = Math.max(
      0,
      queryState.paging.offset - queryState.paging.limit,
    );
    updateQueryState({
      ...queryState,
      paging: { ...queryState.paging, offset: newOffset },
    });
  }, [queryState, updateQueryState]);

  const handleNextPage = useCallback(() => {
    updateQueryState({
      ...queryState,
      paging: {
        ...queryState.paging,
        offset: queryState.paging.offset + queryState.paging.limit,
      },
    });
  }, [queryState, updateQueryState]);

  return {
    handleSortChange,
    handlePrevPage,
    handleNextPage,
  };
}
